import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { APIService } from './api.service';
import { ObserverService } from './observer.service';
import { DatabaseService } from './database.service';
import { ConnectedAccountService } from './connected-account.service';

@Injectable()
export class AuthService {

    constructor(
        private apiService: APIService,
        private storageService: StorageService,
        private observerService: ObserverService,
        private databaseService: DatabaseService,
        private connectedAccountService: ConnectedAccountService
    ) { }

    public disconnectAccount(connected_account_id) {
        return new Promise((resolve, reject) => {
            this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account) => {
                if (connected_account) {
                    this.revokeAccessToken(connected_account).then().catch(() => {});
                }
                this.logout();
                this.connectedAccountService.deleteConnectedAccount(connected_account_id).then(() => {
                    this.connectedAccountService.getConnectedAccounts().then((connected_accounts: []) => {
                        if (connected_accounts) {
                            if (connected_accounts.length === 0) {
                                this.clearPersistentData();
                            }
                        }
                    });
                    resolve(true);
                });
            });
        });
    }

    public disconnectAccounts() {
        return new Promise((resolve, reject) => {
            this.connectedAccountService.getConnectedAccounts().then((connected_accounts: []) => {
                let accounts = connected_accounts;
                for(let i = 0; i < accounts.length; i++) {
                    this.revokeAccessToken(accounts[i]).then().catch(() => {});
                    this.connectedAccountService.deleteConnectedAccount(accounts[i]['id']).then(() => {}).catch(() => {});
                }
                this.logout();
                this.clearPersistentData();
                resolve(true);
            });
        });
    }

    private async clearPersistentData() {
        this.storageService.remove('cart');
        this.storageService.remove('gift_voucher_tax_type');
        this.storageService.remove('sell_screen_mode');
        this.storageService.remove('connected_account_id');
        this.storageService.remove('cash_tracking_session');
        this.storageService.remove('registers_last_updated_date');
        this.storageService.remove('supported_currencies_last_updated_date');
        this.storageService.remove('tax_zones_last_updated_date');
        this.storageService.remove('pricing_tiers_last_updated_date');
        this.storageService.remove('product_categories_last_updated_date');
        this.storageService.remove('event_categories_last_updated_date');
        this.storageService.remove('person_email_lists_last_updated_date');
        this.storageService.remove('countries_last_updated_date');
        this.storageService.remove('couriers_last_updated_date');
        this.storageService.remove('products_last_updated_date');
        this.storageService.remove('measurement_units_last_updated_date');
        this.storageService.remove('users_last_updated_date');
        this.storageService.remove('events_last_updated_date');
        this.storageService.remove('people_last_updated_date');
        this.storageService.remove('organisations_last_updated_date');
        this.storageService.remove('custom_payment_services_last_updated_date');
        this.storageService.remove('stripe_terminal_reader');
        this.storageService.remove('smartpay_reader');
        this.storageService.remove('register_id');
        this.storageService.remove('order_search_params');
        this.storageService.remove('order_payment_surcharge_tax_type');
        this.storageService.remove('order_credit_card_surcharge_rate');
        this.storageService.remove('order_shipping_tax_type');
        this.storageService.remove('order_filter_params');
        this.storageService.remove('organisation_use_contract_price_even_if_more_expensive');
        // NOTE: we don't clear base settings set in appcomponent, otherwise they won't be there after a full disconnect
        await Promise.all(
            this.databaseService.tables.map(table => table.clear())
        );        
    }

    public isLoggedIn(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.storageService.get('connected_account_id').then((connected_account_id) => {
                if (connected_account_id) {
                    resolve(true);
                } else {
                    reject(false);
                }
            })
            .catch((error) => {
                reject(false);
            });
        });
    }

    public login(connected_account_id) {
        return new Promise<void>((resolve, reject) => {
            this.storageService.set('connected_account_id', connected_account_id).then(() => {
                this.observerService.publish('connected_account_id_changed', connected_account_id);
                resolve();
            })
            .catch((error) => {
                reject(error);
            });
        });
    }

    public logout() {
        return new Promise<void>((resolve, reject) => {
            this.storageService.remove('connected_account_id').then(() => {
                this.observerService.publish('logged_out', {});
                resolve();
            })
            .catch((error) => {
                reject(error);
            });
        });
    }

    public renewAccessToken() {
        return new Promise((resolve, reject) => {
            this.storageService.get("connected_account_id").then((connected_account_id) => {
                if (connected_account_id) {
                    this.connectedAccountService.getConnectedAccount(connected_account_id).then((connected_account) => {
                        if (connected_account['site_name'] && connected_account['refresh_token']) {
                            let params = {};
                            params['grant_type'] = "refresh_token";
                            params['refresh_token'] = connected_account['refresh_token'];
                            params['site_name'] = connected_account['site_name'];
                            this.apiService.getAccessToken(params).then((response) => {
                                connected_account['access_token'] = response['access_token'];
                                this.connectedAccountService.updateConnectedAccount(connected_account).then(() => {
                                    this.observerService.publish('connected_account_access_token_changed', connected_account);
                                    resolve(response);
                                })
                                .catch((error) => {
                                    reject(error);
                                });
                            })
                            .catch((error) => {
                                reject(error);
                            });
                        }
                        else {
                            reject();
                        }
                    })
                    .catch((error) => {
                        reject(error);
                    });
                }
                else {
                    reject();
                }
            })
            .catch((error) => {
                reject(error);
            });
        });
    }

    public revokeAccessToken(connected_account) {
        return new Promise<void>((resolve, reject) => {
            if (connected_account.site_name && connected_account.access_token) {
                this.apiService.revokeAccessToken(connected_account.site_name, connected_account.access_token).then(() => {
                    resolve();
                })
                .catch(() => {
                    reject();
                });
            }
            else {
                reject();
            }
        });
    }

}
